import { useState, useEffect } from 'react'
import { interviewRequestService, type InterviewRequest } from '../lib/interviewRequestService'
import { journalistService } from '../lib/journalistService'
import { useAuth } from '../components/AuthContext'
import { useEventYear } from '../components/EventYearContext'

export function useInterviewRequests() {
  const { user: currentUser } = useAuth()
  const { currentYear } = useEventYear()
  const [requests, setRequests] = useState<InterviewRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isJournalist = currentUser?.role === 'journalist'

  const fetchRequests = async () => {
    let journalistDbId: string | undefined
    if (isJournalist && currentUser?.email) {
      const journalist = await journalistService.getByEmail(currentUser.email)
      journalistDbId = journalist?.id
    }
    return interviewRequestService.getAll(currentYear, journalistDbId)
  }

  // Load data on mount and when year changes
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        const requestsData = await fetchRequests()
        setRequests(requestsData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load interview requests')
        console.error('Error loading interview requests:', err)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [currentYear, isJournalist])

  const reloadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const requestsData = await fetchRequests()
      setRequests(requestsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load interview requests')
      console.error('Error loading interview requests:', err)
    } finally {
      setLoading(false)
    }
  }

  const createRequest = async (request: Omit<InterviewRequest, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const newRequest = await interviewRequestService.create(request, currentYear)
      if (newRequest) {
        setRequests(prev => [newRequest, ...prev])
      }
      return newRequest
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create interview request')
      throw err
    }
  }

  const updateRequestStatus = async (id: string, status: 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined') => {
    try {
      const updatedRequest = await interviewRequestService.updateStatus(id, status, currentUser?.id)
      if (updatedRequest) {
        setRequests(prev => prev.map(r => r.id === id ? updatedRequest : r))
      }
      return updatedRequest
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update request status')
      throw err
    }
  }

  const updateRequestPriority = async (id: string, celebrityName: string, priority: 'A' | 'B' | 'C' | 'Unassigned') => {
    try {
      const updatedRequest = await interviewRequestService.updatePriority(id, celebrityName, priority, currentUser?.id)
      if (updatedRequest) {
        setRequests(prev => prev.map(r => r.id === id ? updatedRequest : r))
      }
      return updatedRequest
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update request priority')
      throw err
    }
  }

  const updateJournalistPriority = async (id: string, journalistPriority: 'High' | 'Medium' | 'Low' | null) => {
    try {
      const updatedRequest = await interviewRequestService.updateJournalistPriority(id, journalistPriority)
      if (updatedRequest) {
        setRequests(prev => prev.map(r => r.id === id ? updatedRequest : r))
      }
      return updatedRequest
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update journalist priority')
      throw err
    }
  }

  const markRequestCompleted = async (id: string, celebrityName: string, completed: boolean) => {
    try {
      const updatedRequest = await interviewRequestService.markCompleted(id, celebrityName, completed, currentUser?.id)
      if (updatedRequest) {
        setRequests(prev => prev.map(r => r.id === id ? updatedRequest : r))
      }
      return updatedRequest
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update request completion')
      throw err
    }
  }

  const deleteRequest = async (id: string) => {
    try {
      await interviewRequestService.delete(id)
      setRequests(prev => prev.filter(request => request.id !== id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete request')
      throw err
    }
  }

  const deleteRequestsByJournalist = async (journalistName: string) => {
    try {
      const deletedCount = await interviewRequestService.deleteByJournalistName(journalistName)
      setRequests(prev => prev.filter(request => request.journalistName !== journalistName))
      return deletedCount
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete journalist requests')
      throw err
    }
  }

  const cleanupOrphanedRequests = async () => {
    try {
      const deletedCount = await interviewRequestService.cleanupOrphanedRequests()
      await reloadData()
      return deletedCount
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cleanup orphaned requests')
      throw err
    }
  }

  const getRequestsByJournalist = (journalistName: string) => {
    return requests.filter(request =>
      request.journalistName.toLowerCase().includes(journalistName.toLowerCase())
    )
  }

  const getRequestsByCelebrity = (celebrityName: string) => {
    return requests.filter(request =>
      request.celebrities.some(celeb =>
        celeb.toLowerCase().includes(celebrityName.toLowerCase())
      )
    )
  }

  const getRequestsByStatus = (status: 'new' | 'pending' | 'approved' | 'scheduled' | 'complete' | 'declined') => {
    return requests.filter(request => request.status === status)
  }

  return {
    requests,
    loading,
    error,
    reloadData,
    createRequest,
    updateRequestStatus,
    updateRequestPriority,
    updateJournalistPriority,
    markRequestCompleted,
    deleteRequest,
    deleteRequestsByJournalist,
    cleanupOrphanedRequests,
    getRequestsByJournalist,
    getRequestsByCelebrity,
    getRequestsByStatus
  }
}
